import { EmberClueType, isEmberClueType, TutorialType } from "game-common/models"

// The bookkeeping and the geometry behind the first-run ember camp's five onscreen clues: small
// pointer-and-text boxes that name, in order, the controls the camp's first fire is built with (open
// the pack, switch to crafting, craft the campfire, find it in the pack, Use it).
//
// Pure of pixi on purpose, like crafting_layout.ts beside it -- partly so it can be tested directly
// (gui/tutorial.ts cannot be imported from a unit test: it reaches the sprite graph, which is
// circular), and partly because the awkward part of this feature is not the drawing.
//
// THE AWKWARD PART. Arming and anchoring are separate events, in either order:
//   * ARMED   -- the server said this clue's beat is live. It arms the whole craft chain in one go,
//                at the moment the Lord asks for a fire, because that is when it knows.
//   * ANCHORED -- the control it points at is on screen, and here is where. Two of those three
//                controls are inside an inventory panel the player has not opened yet.
// So a clue waits for its control, is drawn the moment one appears, is redrawn on every rebuild of the
// panel holding it, stops pointing when the panel closes (while staying armed), and holds for its own
// timeout whatever the player does. It is taken down for good by that timeout, by the player
// dismissing it, or by the whole lesson being marked seen.

// How long a clue holds when the server doesn't say. A clue is not a notice like the overworld popups
// (7s): it labels a control the player is being sent to go and find, so it has to outlast the hunt.
export const CLUE_TIMEOUT_MS = 45000

// Space between a clue's text box and the control it points at.
export const CLUE_LEADER_GAP = 18

// How far the box may be pushed down from the top of its host before it is left where it is.
export const CLUE_BOX_MIN_Y = 4
// ...and how close to its host's left edge it may sit. A host that is the whole screen wants a small
// margin; a right-docked panel passes its own (negative) bound.
export const CLUE_BOX_MIN_X = 8

// What each clue says. One imperative per control. No em dashes, same rule as the Lord's spoken lines.
export const CLUE_TEXT: Record<EmberClueType, string[]> = {
    "ember-ready": ["Your axe is ready here.", "Anything on this bar can be held."],
    "ember-wood": ["Ready this wood.", "Strike the campfire with it to light it."],
    "ember-wood-space": ["Make room in your readied items.", "Choose an item and select Unready."],
    "ember-wood-items": ["Open your pack to ready your wood."],
    "ember-wood-pack": ["Choose the wood for its actions."],
    "ember-wood-use": ["Select Ready, then close your pack."],
    "ember-items": ["Open your pack.", "The wood you cut is in there."],
    "ember-crafting": ["Switch to crafting."],
    "ember-recipe": ["Craft a campfire out of the wood."],
    "ember-pack": ["Here it is, in your pack.", ""],
    "ember-use": ["Select Ready, then close your pack.", "Aim at the ground and click to place the fire."],
}

// The five controls in the order the player meets them, which is also how a clue is RETIRED. Drawing
// one of these controls is proof the step before it was taken: the inventory panel cannot draw its
// crafting toggle unless the pack is open, cannot draw a recipe card unless crafting is showing, cannot
// draw a campfire slot unless one was crafted, and cannot draw a Use button unless the item was picked.
// So each anchor report satisfies the clue ahead of it and the chain advances by itself, with no need
// for the server to be told what the player clicked.
//
// The last clue is the exception: nothing drawn on screen proves the campfire left the pack to become a
// fire on the ground, so the server takes the set down when it sees that (ember-tutorial.js).
export const CLUE_ORDER: EmberClueType[] = [
    // "ember-ready" is deliberately NOT in this chain. It names the ready bar when the axe is handed
    // over, which is a beat of its own with nothing before or after it -- putting it in the order
    // would have the pack's clue retire it, which is not what taking the axe means. It ends on its own
    // timeout, or with the rest when the lesson is marked seen.
    "ember-items",
    "ember-crafting",
    "ember-recipe",
    "ember-pack",
    "ember-use",
]

// Which clue, if any, is proven done by having drawn this one's control.
export const clueSatisfiedByDrawing = (tutorialType: EmberClueType): EmberClueType | undefined =>
    tutorialType === "ember-wood-space" ? "ember-wood-items"
        : tutorialType === "ember-wood-pack" ? "ember-wood-items"
        : tutorialType === "ember-wood-use" ? "ember-wood-pack"
        : tutorialType === "ember-wood" ? "ember-wood-use"
        : CLUE_ORDER[CLUE_ORDER.indexOf(tutorialType) - 1]

// What an incoming SHOW_TUTORIAL means, decided in one place so the suppression rule is legible.
//
// `suppressed` is latched true in every ember room (GuiManager.roomUpdated) to keep the OVERWORLD
// popups -- which the intro campaign fires off its own client-side triggers -- from interrupting a
// camp. It must NOT reach the clues: those are the camp's own tutorial, armed a beat at a time by the
// camp's own director, and suppressing them would suppress the very thing the latch protects.
export type TutorialRoute = "clear" | "clue" | "popup" | "suppressed"

export const routeTutorial = (tutorialType: TutorialType | null | undefined, suppressed: boolean): TutorialRoute => {
    if (!tutorialType) {
        return "clear"
    }
    if (isEmberClueType(tutorialType)) {
        return "clue"
    }
    return suppressed ? "suppressed" : "popup"
}

// Where a clue's box and its pointer go, relative to the control.
//
// The box sits to the LEFT of the control, vertically centred on it. Left is the only side with room:
// every clued control is either in the top-right HUD row or inside the right-docked inventory panel.
// `boxMinX` is the left edge the box may not cross, in the host container's own coordinates -- a
// docked panel's container starts hundreds of pixels into the screen, so its clues legitimately sit at
// a negative x. `leader` is the length of the hairline from the box back to the control, and is 0 when
// the clamp left no clear gap to draw one in.
export interface ClueBoxLayout {
    boxX: number
    boxY: number
    leader: number
}

// Which side of its control a clue's box sits on.
//
// "left" is the default and suits everything in the top-right HUD row or the right-docked panel.
// "above" is for a control with nothing to its left to sit in: the READY BAR is at the bottom-left
// corner of the screen, so a box beside it gets clamped straight back on top of the thing it is
// pointing at.
// "below" keeps a top-of-panel control's clue clear of the mobile HUD chips.
export type CluePlacement = "left" | "above" | "below"

export const clueBoxLayout = ({
    anchorX,
    anchorY,
    anchorW = 0,
    anchorH,
    boxW,
    boxH,
    boxMinX = CLUE_BOX_MIN_X,
    placement = "left",
}: {
    anchorX: number
    anchorY: number
    anchorW?: number
    anchorH: number
    boxW: number
    boxH: number
    boxMinX?: number
    placement?: CluePlacement
}): ClueBoxLayout => {
    if (placement === "below") {
        const boxX = Math.max(boxMinX, anchorX + anchorW / 2 - boxW / 2)
        const boxY = Math.max(CLUE_BOX_MIN_Y, anchorY + anchorH + CLUE_LEADER_GAP)
        return { boxX, boxY, leader: Math.max(0, boxY - (anchorY + anchorH)) }
    }
    if (placement === "above") {
        // Centred over the control and sitting on top of it. `leader` is the vertical drop back down
        // to it, and is 0 when the clamp left no clear gap to draw one in.
        const boxX = Math.max(boxMinX, anchorX + anchorW / 2 - boxW / 2)
        const boxY = Math.max(CLUE_BOX_MIN_Y, anchorY - CLUE_LEADER_GAP - boxH)
        return { boxX, boxY, leader: Math.max(0, anchorY - (boxY + boxH)) }
    }
    const boxX = Math.max(boxMinX, anchorX - CLUE_LEADER_GAP - boxW)
    const boxY = Math.max(CLUE_BOX_MIN_Y, anchorY + anchorH / 2 - boxH / 2)
    return { boxX, boxY, leader: Math.max(0, anchorX - (boxX + boxW)) }
}

// Draws one clue at an anchor and hands back how to take it down again, or undefined if it could not
// be drawn. The anchor type is the caller's business: this module only stores it and hands it back.
export type ClueRenderer<A> = (tutorialType: EmberClueType, anchor: A) => (() => void) | undefined

export interface EmberClueDirectorProps<A> {
    draw: ClueRenderer<A>
    // Anchors nothing has to report, because their control is part of the permanent HUD rather than a
    // panel that comes and goes. Resolved on every draw attempt, since the HUD's own visibility moves.
    builtInAnchor?: (tutorialType: EmberClueType) => A | undefined
    defaultTimeoutMs?: number
}

export class EmberClueDirector<A> {
    private props: EmberClueDirectorProps<A>
    // Armed, with each clue's expiry timer.
    private armed: Map<EmberClueType, ReturnType<typeof setTimeout>> = new Map()
    // Where each clue's control was last drawn.
    private anchors: Map<EmberClueType, A> = new Map()
    // What is currently on screen, and how to retract it.
    private drawn: Map<EmberClueType, () => void> = new Map()

    constructor(props: EmberClueDirectorProps<A>) {
        this.props = props
    }

    // The server says this clue's beat is live. Draws it now if its control is already on screen;
    // otherwise remembers it, so it appears the moment the control does.
    arm = (tutorialType: EmberClueType, timeoutMs?: number) => {
        if (this.armed.has(tutorialType)) {
            return
        }
        const hold = timeoutMs && timeoutMs > 0 ? timeoutMs : this.props.defaultTimeoutMs || CLUE_TIMEOUT_MS
        this.armed.set(
            tutorialType,
            setTimeout(() => this.disarm(tutorialType), hold),
        )
        this.refresh(tutorialType)
    }

    // Whatever drew one of the clued controls says where it landed. Called again on every rebuild of
    // the panel it lives in, which is why this redraws rather than skipping: an inventory update
    // throws the whole content tree away, clue and all.
    registerAnchor = (tutorialType: EmberClueType, anchor: A) => {
        this.anchors.set(tutorialType, anchor)
        this.refresh(tutorialType)
    }

    // The control has gone (its panel closed, or this rebuild no longer draws it). The clue stays
    // ARMED -- reopening the panel puts it back -- but nothing is left pointing at nothing.
    clearAnchor = (tutorialType: EmberClueType) => {
        this.anchors.delete(tutorialType)
        this.retract(tutorialType)
    }

    // Every clue down and disarmed. Sent by the server the moment the tutorial counts as seen, and
    // done locally on a room change.
    clearAll = (preserveAnchors = false) => {
        Array.from(this.armed.keys()).forEach(this.disarm)
        Array.from(this.drawn.keys()).forEach(this.retract)
        if (!preserveAnchors) this.anchors.clear()
    }

    // Taken down for good rather than merely undrawn: an expired clue, or one the player dismissed,
    // must not reappear on the next rebuild of the panel it pointed at.
    disarm = (tutorialType: EmberClueType) => {
        const timer = this.armed.get(tutorialType)
        if (timer !== undefined) {
            clearTimeout(timer)
        }
        this.armed.delete(tutorialType)
        this.retract(tutorialType)
    }

    isArmed = (tutorialType: EmberClueType): boolean => this.armed.has(tutorialType)

    isShowing = (tutorialType: EmberClueType): boolean => this.drawn.has(tutorialType)

    private retract = (tutorialType: EmberClueType) => {
        const stop = this.drawn.get(tutorialType)
        this.drawn.delete(tutorialType)
        if (stop) {
            stop()
        }
    }

    private refresh = (tutorialType: EmberClueType) => {
        if (!this.armed.has(tutorialType)) {
            return
        }
        const anchor =
            this.anchors.get(tutorialType) ||
            (this.props.builtInAnchor ? this.props.builtInAnchor(tutorialType) : undefined)
        if (anchor === undefined || anchor === null) {
            return
        }
        // Replace whatever was drawn for this clue before (a rebuilt panel) rather than stacking.
        this.retract(tutorialType)
        const stop = this.props.draw(tutorialType, anchor)
        if (stop) {
            this.drawn.set(tutorialType, stop)
        }
    }
}
